import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, Typography } from '@mui/material';
import SAP_BASIC_01_DeptNode from './SAP_BASIC_01_DeptNode';
import {
  SnapOrgFlowPosition,
  StripDeptPrefix,
  MapDepartmentsToFlowNodes,
  MapLinesToFlowEdges,
  MergeFlowNodesFromMapped,
  ResolveConnectionHandles,
  GetDeptNodeId,
  ORG_DEPT_NODE_WIDTH,
  ORG_DEPT_NODE_CENTER_OFFSET_Y,
  ORG_FLOW_FIT_MAX_ZOOM,
  ORG_FLOW_FIT_PADDING,
  ORG_PAN_SCROLL_SPEED,
  IsOrgFlowZoomWheel,
  ResolveOrgFlowWheelPanDelta,
  ResolveOrgFlowWheelZoom,
  ShouldIgnoreOrgFlowWheel,
  SAP_ORG_EXPORT_HIDE_CLASS,
} from './SAP_BASIC_01_OrgFlowUtils';

const ORG_FLOW_CAPTURE_BG = '#ffffff';
const ORG_FLOW_CAPTURE_ACTIVE_CLASS = 'sap-org-capturing';

/** PNG 다운로드 시 연결 핸들·삭제 버튼·격자·컨트롤 제외 */
const ShouldIncludeInOrgCapture = (node) => {
  if (!(node instanceof HTMLElement)) {
    return true;
  }
  if (node.classList.contains('react-flow__handle')) {
    return false;
  }
  if (node.classList.contains(SAP_ORG_EXPORT_HIDE_CLASS)) {
    return false;
  }
  if (node.closest(`.${SAP_ORG_EXPORT_HIDE_CLASS}`)) {
    return false;
  }
  if (
    node.classList.contains('react-flow__background') ||
    node.classList.contains('react-flow__panel') ||
    node.classList.contains('react-flow__controls') ||
    node.classList.contains('react-flow__minimap')
  ) {
    return false;
  }
  if (
    node.closest('.react-flow__controls') ||
    node.closest('.react-flow__minimap')
  ) {
    return false;
  }
  return true;
};

const BuildOrgCaptureOptions = (extra = {}) => ({
  backgroundColor: ORG_FLOW_CAPTURE_BG,
  pixelRatio: 2,
  cacheBust: true,
  filter: ShouldIncludeInOrgCapture,
  ...extra,
});
const nodeTypes = { department: SAP_BASIC_01_DeptNode };

export const OrgFlowActionsContext = React.createContext(null);

const defaultEdgeOptions = {
  type: 'smoothstep',
  pathOptions: { borderRadius: 8 },
  style: { stroke: '#333', strokeWidth: 2 },
  markerEnd: undefined,
};

function OrgFlowCanvasInner(
  {
    organizationData,
    onDepartmentAdd,
    onDepartmentMove,
    onDepartmentDelete,
    onMemberAssignToDepartment,
    onMemberRemove,
    onLineAdd,
    onLineDelete,
    lineType = 'straight',
    curveness = 0.5,
  },
  ref
) {
  const wrapperRef = useRef(null);
  const prevDeptSetKeyRef = useRef('');
  const { screenToFlowPosition, fitView, getNodes, getViewport, setViewport } =
    useReactFlow();
  const [dragOverDeptId, setDragOverDeptId] = useState(null);

  const departments = organizationData?.departments ?? [];
  const members = organizationData?.members ?? [];
  const lines = organizationData?.lines ?? [];

  const FitCanvasView = useCallback(
    (duration = 220) => {
      requestAnimationFrame(() => {
        fitView({
          padding: ORG_FLOW_FIT_PADDING,
          maxZoom: ORG_FLOW_FIT_MAX_ZOOM,
          minZoom: 0.25,
          duration,
        });
      });
    },
    [fitView]
  );

  /** 현재 보이는 캔버스 영역의 중앙(flow 좌표, 노드 좌상단) */
  const GetFlowPositionAtViewportCenter = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) {
      return { x: 0, y: 0 };
    }
    const rect = el.getBoundingClientRect();
    const center = screenToFlowPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
    return {
      x: SnapOrgFlowPosition(center.x - ORG_DEPT_NODE_WIDTH / 2),
      y: SnapOrgFlowPosition(center.y - ORG_DEPT_NODE_CENTER_OFFSET_Y),
    };
  }, [screenToFlowPosition]);

  /** 부서 목록 변경 감지 (추가 시 fitView 호출하지 않음) */
  const deptSetKey = useMemo(
    () =>
      departments
        .map((d) => GetDeptNodeId(d))
        .sort()
        .join('|'),
    [departments]
  );

  const mappedNodes = useMemo(
    () =>
      MapDepartmentsToFlowNodes(departments, members).map((n) => ({
        ...n,
        data: {
          ...n.data,
          isDragOver:
            dragOverDeptId != null &&
            (n.data?.dept?.id === dragOverDeptId ||
              String(n.id) === String(dragOverDeptId)),
        },
      })),
    [departments, members, dragOverDeptId]
  );

  const membersLayoutKey = useMemo(
    () =>
      members
        .map(
          (m) =>
            `${StripDeptPrefix(m.departmentId)}:${m.id ?? ''}:${m.name ?? m.NAME ?? ''}`
        )
        .sort()
        .join('|'),
    [members]
  );

  const mappedEdges = useMemo(
    () => MapLinesToFlowEdges(lines, lineType, mappedNodes),
    [lines, lineType, mappedNodes]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(mappedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(mappedEdges);

  /** 휠=상하, Shift+휠=좌우, Ctrl/⌘+휠=확대 (zoomOnScroll 비활성 — RF 기본 휠 줌과 충돌 방지) */
  useEffect(() => {
    let pane = null;
    let rafId = 0;

    const OnOrgFlowWheel = (event) => {
      if (ShouldIgnoreOrgFlowWheel(event)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const viewport = getViewport();

      if (IsOrgFlowZoomWheel(event)) {
        const nextZoom = ResolveOrgFlowWheelZoom(event, viewport.zoom, 0.2, 2);
        if (nextZoom !== viewport.zoom) {
          setViewport({ ...viewport, zoom: nextZoom });
        }
        return;
      }

      const { deltaX, deltaY } = ResolveOrgFlowWheelPanDelta(event);
      if (deltaX === 0 && deltaY === 0) {
        return;
      }

      const { x, y, zoom } = viewport;
      setViewport({
        x: x - (deltaX / zoom) * ORG_PAN_SCROLL_SPEED,
        y: y - (deltaY / zoom) * ORG_PAN_SCROLL_SPEED,
        zoom,
      });
    };

    const AttachWheel = () => {
      pane = wrapperRef.current?.querySelector('.react-flow__pane') ?? null;
      if (!pane) {
        rafId = requestAnimationFrame(AttachWheel);
        return;
      }
      pane.addEventListener('wheel', OnOrgFlowWheel, {
        passive: false,
        capture: true,
      });
    };

    AttachWheel();

    return () => {
      cancelAnimationFrame(rafId);
      pane?.removeEventListener('wheel', OnOrgFlowWheel, { capture: true });
    };
  }, [getViewport, setViewport]);

  useEffect(() => {
    setNodes((prev) => MergeFlowNodesFromMapped(mappedNodes, prev));
  }, [mappedNodes, setNodes]);

  useEffect(() => {
    setEdges(mappedEdges);
  }, [mappedEdges, setEdges]);

  /** 인력 추가 등으로 카드 높이가 바뀌면 연결선 재계산 (핸들 위치는 DeptNode에서 useUpdateNodeInternals 처리) */
  useEffect(() => {
    if (!mappedNodes.length) {
      return undefined;
    }
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setEdges(MapLinesToFlowEdges(lines, lineType, mappedNodes));
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
    };
  }, [membersLayoutKey, lines, lineType, mappedNodes, setEdges]);

  /** 저장된 조직도 최초 조회 시에만 전체 맞춤 (부서 추가마다 뷰 이동 없음) */
  useEffect(() => {
    if (departments.length === 0) {
      prevDeptSetKeyRef.current = '';
      return undefined;
    }
    const prevKey = prevDeptSetKeyRef.current;
    prevDeptSetKeyRef.current = deptSetKey;
    if (prevKey !== '') {
      return undefined;
    }
    const isSavedLayout =
      departments.length > 1 ||
      departments.some((d) => (d.x ?? 0) !== 0 || (d.y ?? 0) !== 0);
    if (!isSavedLayout) {
      return undefined;
    }
    const t = setTimeout(() => FitCanvasView(200), 120);
    return () => clearTimeout(t);
  }, [deptSetKey, FitCanvasView, departments]);

  const onConnect = useCallback(
    (connection) => {
      const { source, target, sourceHandle, targetHandle } = connection;
      if (!source || !target || source === target || !onLineAdd) return;
      const exists = (lines || []).some((line) => {
        const s = StripDeptPrefix(line.sourceId ?? line.source);
        const t = StripDeptPrefix(line.targetId ?? line.target);
        return (
          (s === source && t === target) || (s === target && t === source)
        );
      });
      if (exists) return;
      const sourceNode = nodes.find((n) => n.id === source);
      const targetNode = nodes.find((n) => n.id === target);
      const resolved = ResolveConnectionHandles(
        sourceNode,
        targetNode,
        sourceHandle,
        targetHandle
      );
      onLineAdd({
        id: `line_${Date.now()}_${Math.random()}`,
        sourceId: source,
        targetId: target,
        startAnchor: resolved.sourceHandle || 'auto',
        endAnchor: resolved.targetHandle || 'auto',
        path: lineType,
        curveness: lineType === 'smooth' ? curveness : 0,
      });
    },
    [lines, lineType, curveness, onLineAdd, nodes]
  );

  const onNodeDragStop = useCallback(
    (_, node) => {
      if (!onDepartmentMove || !node?.id) return;
      const dept = departments.find(
        (d) => StripDeptPrefix(d.id) === node.id || String(d.id) === node.id
      );
      if (!dept) return;
      onDepartmentMove(
        dept.id,
        SnapOrgFlowPosition(node.position.x),
        SnapOrgFlowPosition(node.position.y)
      );
    },
    [departments, onDepartmentMove]
  );

  const onEdgesDelete = useCallback(
    (deleted) => {
      if (!onLineDelete) return;
      deleted.forEach((edge) => onLineDelete(edge.id));
    },
    [onLineDelete]
  );

  const onDragOverPane = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDropPane = useCallback(
    (e) => {
      e.preventDefault();
      setDragOverDeptId(null);
      try {
        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr || !onDepartmentAdd) return;
        const data = JSON.parse(dataStr);
        if (data.type !== 'department') return;
        const position = GetFlowPositionAtViewportCenter();
        const depCd = data.item?.depCd || data.item?.DEP_CD || null;
        onDepartmentAdd({
          ...data.item,
          x: position.x,
          y: position.y,
          id: depCd || data.item?.id || `dept_${Date.now()}_${Math.random()}`,
        });
      } catch (err) {
        console.error('부서 드롭 처리 오류:', err);
      }
    },
    [onDepartmentAdd, GetFlowPositionAtViewportCenter]
  );

  const flowActions = useMemo(
    () => ({
      onDepartmentDelete,
      onMemberRemove,
      onMemberDrop: (member, departmentId) => {
        setDragOverDeptId(null);
        onMemberAssignToDepartment?.(member, departmentId);
      },
      onMemberDragOver: (departmentId) => setDragOverDeptId(departmentId),
      onMemberDragLeave: () => setDragOverDeptId(null),
    }),
    [onDepartmentDelete, onMemberRemove, onMemberAssignToDepartment]
  );

  useImperativeHandle(ref, () => ({
    captureImage: async () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return null;
      wrapper.classList.add(ORG_FLOW_CAPTURE_ACTIVE_CLASS);
      try {
        const flowNodes = getNodes();
        const { toPng } = await import('html-to-image');
        if (!flowNodes.length) {
          const viewport = wrapper.querySelector('.react-flow__viewport');
          if (!viewport) return null;
          return toPng(viewport, BuildOrgCaptureOptions());
        }
        const bounds = getNodesBounds(flowNodes);
        const width = Math.max(bounds.width + 80, 400);
        const height = Math.max(bounds.height + 80, 300);
        const viewport = getViewportForBounds(
          bounds,
          width,
          height,
          0.5,
          2,
          0.12
        );
        const viewportEl = wrapper.querySelector('.react-flow__viewport');
        if (!viewportEl) return null;
        return toPng(
          viewportEl,
          BuildOrgCaptureOptions({
            width,
            height,
            style: {
              width: `${width}px`,
              height: `${height}px`,
              transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
            },
          })
        );
      } finally {
        wrapper.classList.remove(ORG_FLOW_CAPTURE_ACTIVE_CLASS);
      }
    },
    fitView: () => FitCanvasView(300),
  }));

  return (
    <OrgFlowActionsContext.Provider value={flowActions}>
      <Box
        ref={wrapperRef}
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          height: '100%',
          bgcolor: '#f6f7f8',
          '& .react-flow__node-department': {
            width: `${ORG_DEPT_NODE_WIDTH}px !important`,
            minWidth: `${ORG_DEPT_NODE_WIDTH}px`,
            maxWidth: `${ORG_DEPT_NODE_WIDTH}px`,
            height: 'auto !important',
            padding: 0,
            border: 'none',
            background: 'transparent',
            boxShadow: 'none',
          },
          '& .react-flow__node-department .sap-org-dept-node': {
            width: `${ORG_DEPT_NODE_WIDTH}px`,
          },
          '& .react-flow__node-department .react-flow__handle': {
            opacity: 1,
          },
          [`&.${ORG_FLOW_CAPTURE_ACTIVE_CLASS} .react-flow__handle`]: {
            visibility: 'hidden',
          },
          [`&.${ORG_FLOW_CAPTURE_ACTIVE_CLASS} .${SAP_ORG_EXPORT_HIDE_CLASS}`]: {
            visibility: 'hidden',
          },
          [`&.${ORG_FLOW_CAPTURE_ACTIVE_CLASS} .react-flow__background`]: {
            display: 'none',
          },
        }}
      >
        <ReactFlow
          nodeOrigin={[0, 0]}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStop={onNodeDragStop}
          onEdgesDelete={onEdgesDelete}
          onDragOver={onDragOverPane}
          onDrop={onDropPane}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          snapToGrid
          snapGrid={[20, 20]}
          deleteKeyCode={['Backspace', 'Delete']}
          defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
          panOnScroll={false}
          selectionOnDrag={false}
          panOnDrag={[2]}
          zoomOnScroll={false}
          zoomOnPinch={false}
          minZoom={0.2}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#d1d5db" gap={24} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeColor="#137fec"
            maskColor="rgba(241, 245, 249, 0.85)"
            pannable
            zoomable
          />
          {departments.length === 0 && (
            <Panel position="top-center">
              <Typography
                sx={{
                  mt: 8,
                  color: '#94a3b8',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                왼쪽에서 부서를 드래그하여 배치하세요
              </Typography>
            </Panel>
          )}
        </ReactFlow>
      </Box>
    </OrgFlowActionsContext.Provider>
  );
}

const OrgFlowCanvas = forwardRef(OrgFlowCanvasInner);

const SAP_BASIC_01_OrgFlow = forwardRef(function SAP_BASIC_01_OrgFlow(props, ref) {
  return (
    <ReactFlowProvider>
      <OrgFlowCanvas {...props} ref={ref} />
    </ReactFlowProvider>
  );
});

export default SAP_BASIC_01_OrgFlow;
